import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ButtonGroup, Button, Grid } from '@material-ui/core';
import Table, { columnType } from '../../components/ClearingTable/Table';
import Text from '../../components/Typography/Text';
import BalanceCard from '../../components/Cards/BalanceCard';
import ChartSummary from './Chart/ChartSummary';
import { notifyInfo, notifyError } from '../../components/Messages/Notification';

import { formatCurrency } from '../../lib/fmt';

import { ReactComponent as AccountsSetup } from '../../assets/icons/account-setup.svg';

import { listBalance } from '../../services/BalanceService';
import { getSystemDate } from '../../services/ProfileService';
import { getCurrentUser } from '../../services/AuthService';
import { getAccountInfo } from '../../services/ClientAccountService';

import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderAlign,
} from '../../components/Table/CustomBodyRender';
import { formatPbDate } from '../../lib/fmt';

export default function Dashboard() {
  const { Status = 'Inactive', UserType } = getCurrentUser();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = React.useState({
    chart: false,
    grid: true,
    tile: false,
  });
  const [totalBalance, setTotalBalance] = useState(0);
  const [clientStatus, setClientStatus] = useState(null);
  const [subLabel, setSubLabel] = useState('');
  let clientDataStatus = '';

  const columns = [
    {
      // Already converted to string in handlesearch
      name: 'date',
      label: 'Date',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountStatus',
      label: 'Account Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'tdCashBalance',
      label: 'TD Cash Balance',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tdLongMarketValue',
      label: 'TD Long Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tdShortMarketValue',
      label: 'TD Short Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tdEquity',
      label: 'TD Equity',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'tdAdjustedBalance',
      label: 'TD Adjusted Balance',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sdCashBalance',
      label: 'SD Cash Balance',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sdLongMarketValue',
      label: 'SD Long Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sdShortMarketValue',
      label: 'SD Short Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sdEquity',
      label: 'SD Equity',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'sdAdjustedBalance',
      label: 'SD Adjusted Balance',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
  };

  const loadBalance = async () => {
    let totalBalance = 0;

    try {
      setLoading(true);

      const systemDate = await getSystemDate();

      const searchData = {
        fromDate: systemDate,
        toDate: systemDate,
      };

      const { balancesList } = await listBalance(searchData);

      totalBalance = balancesList.reduce((partialSum, a) => {
        return partialSum + parseFloat(a.tdCashBalance);
      }, 0);

      setTotalBalance(totalBalance || 0);

      let rowsCopy = balancesList;

      const rowsCopyOutput = rowsCopy.map((data) => {
        let tempDate = formatPbDate(data.date);
        data.date = tempDate;

        return data;
      });

      setRows(rowsCopyOutput);

      notifyInfo(
        balancesList.length > 0
          ? balancesList.length + ' search result(s)'
          : 'No records found.'
      );
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccountClientStatus = async () => {
    if (
      !['In Progress', 'For Review', 'Pending'].includes(Status) &&
      UserType !== 'Client'
    ) {
      setClientStatus(Status);
      loadBalance();
      return;
    }

    try {
      const res = await getAccountInfo();
      setClientStatus(res.status);
      clientDataStatus = res.status;
      if (clientDataStatus === 'In Progress') {
        setSubLabel('Please complete your application.');
      } else if (clientDataStatus === 'Pending') {
        setSubLabel(
          'Your application has been completed. It is now in pending approval.'
        );
      } else if (clientDataStatus === 'For Review') {
        setSubLabel('Your application has been completed. It is now under review');
      } else {
        loadBalance();
      }
      return res.status;
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAccountClientStatus();
    // eslint-disable-next-line
  }, []);

  const handleToggleView = async (dashboardView) => {
    setView({
      chart: dashboardView === 'chart',
      grid: dashboardView === 'grid',
      tile: dashboardView === 'tile',
    });
  };

  if (clientStatus === null) {
    return <></>;
  }

  return (
    <div className="page-c">
      {!['In Progress', 'For Review', 'Pending'].includes(Status) &&
      (!['In Progress', 'For Review', 'Pending'].includes(clientStatus) ||
        UserType !== 'Client') ? (
        <>
          <div>
            <Text mt={8} variant="h1" label="Dashboard" />
            <Text
              mt={10}
              variant="subtitle2"
              label={
                view.chart ? 'Account Summary Dashboard' : 'Balance Summary Dashboard'
              }
            />
          </div>
          {rows.length > 0 && (
            <div className="grd-row grd-align-end">
              <div className="grd-cell">
                {!view.chart && (
                  <div className="total-balance-card">
                    <Text variant="subtitle2" label="Total Balance" />
                    <Text mt={8} variant="h1" label={formatCurrency(totalBalance)} />
                  </div>
                )}
              </div>

              <div className="grd-cell-none">
                <ButtonGroup disableElevation color="primary">
                  <Button
                    variant={view.grid ? 'contained' : 'outlined'}
                    onClick={() => handleToggleView('grid')}
                  >
                    Grid
                  </Button>
                  <Button
                    variant={view.chart ? 'contained' : 'outlined'}
                    onClick={() => handleToggleView('chart')}
                  >
                    Chart
                  </Button>
                  <Button
                    variant={view.tile ? 'contained' : 'outlined'}
                    onClick={() => handleToggleView('tile')}
                  >
                    Tile
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          )}
          <div className="mt-20">
            {rows.length > 0 && view.grid ? (
              <Table
                title={'Balance Summary'}
                data={rows}
                columns={columns}
                options={options}
              />
            ) : rows.length > 0 && view.tile ? (
              <div className="card-wrapper">
                <Grid container spacing={3}>
                  {rows.map((balance, key) => {
                    return (
                      <Grid
                        item
                        sm={12}
                        md={6}
                        lg={6}
                        xl={4}
                        key={balance.accountName + '-' + key}
                      >
                        <BalanceCard data={balance} />
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
            ) : view.chart ? (
              <div className="no-data-wrapper">
                <ChartSummary />
              </div>
            ) : (
              <div className="no-data-wrapper">
                <Text
                  variant="body1"
                  label={
                    !loading && rows.length === 0
                      ? 'Sorry, no data found'
                      : 'Fetching data, please wait...'
                  }
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="dashboard-c">
          <div className="dashboard-b">
            <div className="grd-row nm">
              <NavLink className={'grd-cell'} to="/app/registration/account">
                <AccountsSetup />
                <div className="mt-20">
                  <Text variant={'subtitle1'} label="Client Account" />
                </div>
              </NavLink>
            </div>
            <div className="grd-row mt-30">
              <div className="mt-30">
                <Text variant={'h3'} label={subLabel} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
